import classNames from "classnames";
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { connect, ConnectedProps } from "react-redux";
import { useHistory } from "react-router-dom";

import Button from "components/button/Button";
import Info from "components/icons/Info";
import { Licenses } from "components/icons/Licenses";
import CircleNumber from "components/products/CircleWithNumber";
import style from "components/products/getting-started.scss";
import StepCompletedText from "components/products/StepCompletedText";
import StepHeader from "components/products/StepHeader";
import { ALL_MCS_ROUTE } from "components/router/Routes";
import { AUTH_EMS_CREATE_ENTITLEMENT, AUTH_MC_VIEW } from "domain/authority";
import { BmpOpOnboardingSteps, OnboardingProductType } from "domain/onboardings";
import { hybridMcService } from "services/hybrid-mc/HybridMcService";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { Action } from "services/statistics/UsageStatisticsService";
import { getCurrentTenantDetails } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

interface Props {
    stepIndex: number;
    menuExpanded: boolean;
    stepCompleted: boolean;
    stepActive: boolean;
    stepClickable: boolean;
    saveOnboardingStep: (onboarding: Onboarding) => void;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
}

interface Step {
    description: string | JSX.Element;
    subStep?: string[];
    locationState?: { toCreateViews: boolean } | { autoOpenCreateEntitlement: boolean };
    onClick?: () => void;
    usageStatisticsAction?: Action;
    info?: string | JSX.Element;
}

const LaunchBmpOp = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [showContent, setShowContent] = React.useState<boolean>(false);
    const stepName = BmpOpOnboardingSteps.STEP_LAUNCHING_BMPOP;
    const theme = useTheme();
    const history = useHistory();

    const { data: { offlineSyncEnabled } = { offlineSyncEnabled: false } } = useQuery(["offlineSyncEnabled"], () =>
        fetchSyncIntervalData(getCurrentTenantDetails().uuid)
    );

    const fetchSyncIntervalData = async (tenantUuid: string) => {
        try {
            const syncIntervalData = await hybridMcService.fetchIntervals(tenantUuid);
            return {
                offlineSyncEnabled: syncIntervalData.offlineSync,
            };
        } catch {
            return { offlineSyncEnabled: false };
        }
    };

    const clickEvent = () => {
        props.stepClickable && setShowContent(!showContent);
    };

    const saveOnboardingStep = () => {
        props.saveOnboardingStep({
            product: OnboardingProductType.BMPOP,
            step: BmpOpOnboardingSteps.STEP_LAUNCHING_BMPOP,
        });
    };

    React.useEffect(() => {
        if (props.stepInProgressIndex !== props.stepIndex) {
            setShowContent(false);
        }
    }, [props.stepInProgressIndex]);

    React.useEffect(() => {
        if (props.menuExpanded && props.stepClickable) {
            setShowContent(true);
        }
    }, [props.menuExpanded]);

    const haspBanner = (textToShow: string) => {
        return (
            <div className={classNames(style.alertBox, style.subDescriptionMargin)}>
                <div>
                    <Info borderColor={theme.contentBackgroundColor} color={theme.sortIconActiveColor} size={"18"} />
                </div>
                <div className={style.bannerText}>{textToShow}</div>
            </div>
        );
    };

    const launchBmpOpSteps: Step[] = [
        {
            description: (
                <>
                    {t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.first")}
                    {haspBanner(t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.note"))}
                </>
            ),
        },
        {
            description: t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.second"),
            subStep: [
                t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.secondSubStep1"),
                t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.secondSubStep2"),
            ],
            info: (
                <div className={classNames(style.hybridInfoBackground, style.bannerText)}>
                    <Trans
                        i18nKey={t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.offlineRegistrationTitle")}
                        components={[<strong key="bold" />]}
                    />
                    <ul className={classNames(style.subDescriptionMargin, style.blueBullets)}>
                        <li>
                            {t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.offlineRegistrationTitleSubStep1")}
                        </li>
                        <li>
                            {t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.offlineRegistrationTitleSubStep2")}
                        </li>
                        <li>
                            {t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.offlineRegistrationTitleSubStep3")}
                        </li>
                    </ul>
                </div>
            ),
        },
        {
            description: (
                <>
                    <Trans
                        i18nKey={t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.third")}
                        values={{
                            linkText: t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.onPremiseLinkText"),
                        }}
                        components={[
                            <a
                                key="link"
                                onClick={() => {
                                    window.location.href = ALL_MCS_ROUTE.path;
                                }}
                            />,
                        ]}
                    />
                </>
            ),
        },
        {
            description: (
                <>
                    <Trans
                        i18nKey={t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.fourth")}
                        values={{
                            linkText: t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.deliverEntitlementText"),
                        }}
                        components={[
                            <a
                                key="link"
                                onClick={() => {
                                    const entitlementLink = {
                                        featureLicenses: [],
                                        authorities: [AUTH_MC_VIEW, AUTH_EMS_CREATE_ENTITLEMENT],
                                        all: true,
                                        linkTitle: t("Onboarding.nextSteps.createEntitlement"),
                                        icon: <Licenses color={theme.iconFillColor} />,
                                        routePath: ALL_MCS_ROUTE.path,
                                        testId: testIds.workArea.onboarding.bmpSetupContainer.entitlementsLink,
                                        usageStatisticsAction: Action.ADD_ENTITLEMENT,
                                        toShow: true,
                                        locationState: { autoOpenCreateEntitlement: true },
                                    };

                                    history.push(
                                        entitlementLink?.routePath ?? ALL_MCS_ROUTE.path,
                                        entitlementLink?.locationState
                                    );
                                }}
                            />,
                        ]}
                    />
                </>
            ),
            locationState: { autoOpenCreateEntitlement: true },
            usageStatisticsAction: Action.ADD_ENTITLEMENT,
        },
    ];

    function createContent() {
        return (
            <>
                <div className={style.stepsContainer}>
                    {launchBmpOpSteps.map((step, index) => (
                        <div key={index} className={style.subStepsToFollowLayout}>
                            <CircleNumber number={index + 1} subStep={true} />
                            <div className={style.stepPadding}>
                                <div>
                                    {step.description}
                                    {step.subStep && (
                                        <div>
                                            <ul>
                                                {step.subStep.map((subStep, index) => (
                                                    <li
                                                        key={index}
                                                        className={classNames(style.subStepList, style.blueBullets)}
                                                    >
                                                        {subStep}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {offlineSyncEnabled && step.info && (
                                        <div className={style.subStepList}>{step.info}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className={style.markComplete}>
                    {props.stepCompleted ? (
                        <StepCompletedText
                            successText={t("Onboarding.gettingStartedFlow.completed")}
                            testId={testIds.workArea.onboarding.step.statusLabel}
                            dataType={OnboardingProductType.BMPOP + "_" + stepName}
                        />
                    ) : (
                        <Button
                            variant={"PRIMARY"}
                            loadingAnimation={props.stepInProgressIndex === props.stepIndex}
                            disabled={props.stepInProgressIndex === props.stepIndex}
                            className={classNames(buttons.primaryButtonWithoutIcon)}
                            onClick={() => {
                                props.saveStepInProgressIndex(props.stepIndex);
                                saveOnboardingStep();
                            }}
                            testId={testIds.workArea.onboarding.step.primaryButton}
                            data-type={OnboardingProductType.BMPOP + "_" + stepName}
                        >
                            {props.stepInProgressIndex === props.stepIndex
                                ? t("Onboarding.saving")
                                : t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.markComplete")}
                        </Button>
                    )}
                </div>
            </>
        );
    }

    return (
        <StepHeader
            success={props.stepCompleted}
            showStep={showContent}
            inactive={!props.stepClickable}
            title={t("Onboarding.bmpOp.gettingStarted.steps.launchingBmpOp.title")}
            stepIndex={props.stepIndex}
            menuExpanded={props.menuExpanded}
            showContent={showContent}
            clickEvent={clickEvent}
            content={createContent()}
            type={OnboardingProductType.BMPOP}
            stepName={stepName}
        />
    );
};

export default connector(LaunchBmpOp);
